import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function FeaturesPage() {
  const features = [
    {
      number: '01',
      icon: 'ri-user-line',
      title: '学習形態を選ばない個別学習履歴',
      catchphrase: '個別でも、少人数でも、教室単位でも。',
      description: '個別学習・少人数グループ・教室全体の学習など、どの学習形態でも、一人ひとりの学習履歴を自動で記録します。オンラインでの学びの積み重ねを、子ども単位で追いかけられます。',
      color: 'from-teal-500 to-blue-500',
      bgColor: 'bg-teal-50'
    },
    {
      number: '02',
      icon: 'ri-file-list-3-line',
      title: '学習履歴簿の自動出力',
      catchphrase: '日々の学びが、そのまま報告資料に。',
      description: 'いつ・だれが・どの教科を、どのくらい学んだか。ボラタス上でのオンライン学習の記録を、学習履歴簿として出力できます。行政への実績報告や、不登校支援のモニタリング資料にもそのまま活用できます。',
      color: 'from-blue-500 to-indigo-500',
      bgColor: 'bg-blue-50'
    },
    {
      number: '03',
      icon: 'ri-video-chat-line',
      title: 'すぐつながるオンライン学習',
      catchphrase: '「困った今」に、すぐ学べる。',
      description: 'こどもの居場所や学校と、学習サポーター（大学生・社会人）をオンラインでつなぎ、すぐに学習を始められます。家庭学習の補完、不登校の子の学び直しなどにも対応できます。',
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50'
    },
    {
      number: '04',
      icon: 'ri-play-circle-line',
      title: 'ボタンひとつで学習スタート',
      catchphrase: '準備いらずで、すぐ授業。',
      description: 'URL発行やツール設定などの面倒な準備は不要。画面のボタンを押すだけでオンライン学習が始まるので、ITが苦手なスタッフや子どもでも、迷わず使えます。',
      color: 'from-green-500 to-teal-500',
      bgColor: 'bg-green-50'
    },
    {
      number: '05',
      icon: 'ri-coin-line',
      title: '謝金まで自動でおまかせ',
      catchphrase: '計算しない。間違えない。',
      description: '記録された学習時間をもとに、ボランティア講師への謝金を自動で計算・管理。事務局側での集計や振り分けの手間を減らし、「どの講師に、どれだけ支払うか」がすぐに分かります。',
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-50'
    },
    {
      number: '06',
      icon: 'ri-shield-user-line',
      title: 'ニックネームで安心して参加',
      catchphrase: '名前を出さなくても、つながれる。',
      description: 'ボランティアも、子どもも、本名ではなくニックネームで参加できます。オンラインだからこそ、プライバシーと安心感を大切に。必要な情報は運営側だけが把握しつつ、現場では呼びやすい名前で関われます。',
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-50'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4 bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            ボラタスでできること
          </h1>
          <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
            ボラタスは、こどもの居場所・学校・支援団体のための
            <br className="hidden md:block" />
            「オンライン学習」と「学習履歴管理」を一つにしたサービスです。
          </p>
          <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
            日々のオンライン学習の様子が、そのまま個別の学習履歴として残り、出力できます。
          </p>
          <p className="text-sm md:text-base text-teal-700 leading-relaxed font-medium">
            支援者の手間や負担を減らし、こどもに向き合う時間を増やすために、このサービスをつくりました。
          </p>
          <div className="w-24 h-1 bg-teal-500 mx-auto mt-8"></div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className={`${feature.bgColor} rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-shadow duration-300`}
              >
                {/* Number Badge */}
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center shadow-md`}>
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="w-10 h-10 bg-gray-900 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {feature.number}
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>

                {/* Catchphrase */}
                <p className="text-base font-semibold text-teal-600 mb-4">
                  {feature.catchphrase}
                </p>

                {/* Description */}
                <p className="text-sm text-gray-700 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Flow Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-12">
            シンプルな流れで、確かな記録
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Step 1 */}
            <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-gray-200">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-play-circle-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">ボタンを押す</h3>
              <p className="text-sm text-gray-600">準備不要で学習開始</p>
            </div>

            {/* Arrow */}
            <div className="hidden md:flex items-center justify-center">
              <i className="ri-arrow-right-line text-3xl text-teal-500"></i>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-gray-200">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-video-chat-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">学習する</h3>
              <p className="text-sm text-gray-600">オンラインで学びを深める</p>
            </div>

            {/* Arrow */}
            <div className="hidden md:flex items-center justify-center">
              <i className="ri-arrow-right-line text-3xl text-teal-500"></i>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-xl p-6 text-center shadow-sm border border-gray-200">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-list-3-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">記録が残る</h3>
              <p className="text-sm text-gray-600">自動で履歴簿に変換</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-4">
            こんな場面で活躍します
          </h2>
          <p className="text-center text-gray-600 mb-12">
            現場が回ることが、こどもが安心して学べる環境の土台になります
          </p>
          
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-6 border border-teal-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-home-heart-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">こどもの居場所での学習支援</h3>
                  <p className="text-sm text-gray-700">
                    放課後や休日に、オンラインで学習サポーターとつながり、宿題や復習をサポート。学習履歴は自動で記録され、報告資料としても活用できます。
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-school-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">不登校支援のモニタリング</h3>
                  <p className="text-sm text-gray-700">
                    学校に通えない子どもたちの学習状況を可視化。いつ、どの教科を、どれだけ学んだかを記録し、支援の効果測定や行政報告に活用できます。
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-team-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">支援団体の実績管理</h3>
                  <p className="text-sm text-gray-700">
                    複数の子どもや複数の講師が関わる学習支援活動を一元管理。謝金計算も自動化され、事務作業の負担を大幅に軽減します。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-teal-600 to-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            ボラタスで、学びの記録を始めませんか？
          </h2>
          <p className="text-base text-white/90 mb-8">
            オンライン学習と学習履歴管理を、ひとつのサービスで。
          </p>
          <a 
            href="https://boratasu.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
          >
            サービスを使ってみる
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
